import http from './http';
import { cleanParams } from './utils';

// Helper to extract a clean error message and throw
function handleError(error) {
  const msg = error?.response?.data?.message || error?.message || 'Request failed';
  throw new Error(msg);
}

// ===================
// INVENTORY ITEMS API
// ===================

// GET /api/v1/inventory/items - Get all inventory items with filtering and pagination
export async function getAllInventoryItems(params = {}) {
  try {
    // Clean parameters to remove empty values
    const cleanedParams = cleanParams(params);
    const res = await http.get('/api/v1/inventory/items', { params: cleanedParams });
    return { 
      success: true,
      data: res.data 
    };
  } catch (e) { 
    return { 
      success: false, 
      message: e?.response?.data?.message || e?.message || 'Failed to fetch inventory items' 
    };
  }
}

// POST /api/v1/inventory/items - Create new inventory item
export async function createInventoryItem(itemData) {
  try {
    const res = await http.post('/api/v1/inventory/items', itemData);
    return { 
      success: true,
      data: res.data 
    };
  } catch (e) { 
    return { 
      success: false, 
      message: e?.response?.data?.message || e?.message || 'Failed to create inventory item' 
    };
  }
}

// GET /api/v1/inventory/items/:id - Get inventory item by ID
export async function getInventoryItemById(id) {
  try {
    const res = await http.get(`/api/v1/inventory/items/${id}`);
    return { 
      success: true,
      data: res.data 
    };
  } catch (e) { 
    return { 
      success: false, 
      message: e?.response?.data?.message || e?.message || 'Failed to fetch inventory item' 
    };
  }
}

// PUT /api/v1/inventory/items/:id - Update inventory item
export async function updateInventoryItem(id, updateData) {
  try {
    const res = await http.put(`/api/v1/inventory/items/${id}`, updateData);
    return { 
      success: true,
      data: res.data 
    };
  } catch (e) { 
    return { 
      success: false, 
      message: e?.response?.data?.message || e?.message || 'Failed to update inventory item' 
    };
  }
}

// DELETE /api/v1/inventory/items/:id - Delete inventory item (soft delete)
export async function deleteInventoryItem(id) {
  try {
    const res = await http.delete(`/api/v1/inventory/items/${id}`);
    return { 
      success: true,
      data: res.data 
    };
  } catch (e) { 
    return { 
      success: false, 
      message: e?.response?.data?.message || e?.message || 'Failed to delete inventory item' 
    };
  }
}

// ===================
// STOCK MANAGEMENT API
// ===================

// POST /api/v1/inventory/items/:id/add-stock - Add stock to inventory item
export async function addStock(id, stockData) {
  try {
    const res = await http.post(`/api/v1/inventory/items/${id}/add-stock`, stockData);
    return { 
      success: true,
      data: res.data 
    };
  } catch (e) { 
    return { 
      success: false, 
      message: e?.response?.data?.message || e?.message || 'Failed to add stock' 
    };
  }
}

// POST /api/v1/inventory/items/:id/remove-stock - Remove stock from inventory item
export async function removeStock(id, stockData) {
  try {
    const res = await http.post(`/api/v1/inventory/items/${id}/remove-stock`, stockData);
    return { 
      success: true,
      data: res.data 
    };
  } catch (e) { 
    return { 
      success: false, 
      message: e?.response?.data?.message || e?.message || 'Failed to remove stock' 
    };
  }
}

// GET /api/v1/inventory/items/:id/movements - Get stock movements for an inventory item
export async function getStockMovements(id, params = {}) {
  try {
    // Clean parameters to remove empty values
    const cleanedParams = cleanParams(params);
    const res = await http.get(`/api/v1/inventory/items/${id}/movements`, { params: cleanedParams });
    return { 
      success: true,
      data: res.data 
    };
  } catch (e) { 
    return { 
      success: false, 
      message: e?.response?.data?.message || e?.message || 'Failed to fetch stock movements' 
    };
  }
}

// ===================
// INVENTORY ALERTS & STATS API
// ===================

// GET /api/v1/inventory/alerts - Get inventory alerts (low stock, expiring, expired items)
export async function getInventoryAlerts() {
  try {
    const res = await http.get('/api/v1/inventory/alerts');
    return { 
      success: true,
      data: res.data 
    };
  } catch (e) { 
    return { 
      success: false, 
      message: e?.response?.data?.message || e?.message || 'Failed to fetch inventory alerts' 
    };
  }
}

// GET /api/v1/inventory/stats - Get inventory statistics
export async function getInventoryStats() {
  try {
    const res = await http.get('/api/v1/inventory/stats');
    return { 
      success: true,
      data: res.data 
    };
  } catch (e) { 
    return { 
      success: false, 
      message: e?.response?.data?.message || e?.message || 'Failed to fetch inventory statistics' 
    };
  }
}

// GET /api/v1/inventory/test - Test route
export async function testInventoryConnection() {
  try {
    const res = await http.get('/api/v1/inventory/test');
    return { 
      success: true,
      data: res.data 
    };
  } catch (e) { 
    return { 
      success: false, 
      message: e?.response?.data?.message || e?.message || 'Failed to test inventory connection' 
    };
  }
}

// Default export object with all inventory API functions
const inventoryAPI = {
  // Inventory Items
  getAllInventoryItems,
  createInventoryItem,
  getInventoryItemById,
  updateInventoryItem,
  deleteInventoryItem,
  
  // Stock Management
  addStock,
  removeStock,
  getStockMovements,
  
  // Alerts & Stats
  getInventoryAlerts,
  getInventoryStats,
  
  // Utility
  testInventoryConnection
};

export default inventoryAPI;